import { useState, useCallback, useId } from 'react';

import styles from './booking-widget.module.scss';

import Icons from '@components/Icons';
import type { guestsLimit, roomQty, roomsLimit } from '@lib/types';

const BookingRoomsComponent = ({
    open,
    setOpen,
    roomQty,
    config,
    setRoomQtyFunc,
    newDefaultMinimalRoomFunc,
}: {
    open: boolean;
    setOpen: (v: boolean) => void;
    roomQty: roomQty[];
    config: {
        limits: {
            rooms: roomsLimit;
            guests: guestsLimit;
        };
        numToWord: string[];
    };

    setRoomQtyFunc: (roomQty: roomQty[]) => void;
    newDefaultMinimalRoomFunc: () => roomQty;
}) => {
    const dialogId = useId();

    //Add new room to the roomQty array
    const changeRoomUp = useCallback(() => {
        setRoomQtyFunc([...roomQty, newDefaultMinimalRoomFunc()]);
    }, [roomQty, setRoomQtyFunc, newDefaultMinimalRoomFunc]);

    // Remove the last room from roomQty array
    const changeRoomDown = useCallback(() => {
        setRoomQtyFunc(roomQty.slice(0, -1));
    }, [roomQty, setRoomQtyFunc]);

    // Guests Controller adds or removes guests from a room
    const setRoomGuests = useCallback(
        (roomIndex: number, ppltype: keyof roomQty, direction: boolean) => {
            setRoomQtyFunc(
                roomQty.map((room, i) => {
                    return i === roomIndex
                        ? {
                              ...room,
                              [ppltype]: direction
                                  ? room[ppltype] + 1
                                  : room[ppltype] - 1,
                          }
                        : room;
                }),
            );
        },
        [roomQty, setRoomQtyFunc],
    );

    const changeRoomGuestsUp = useCallback(
        (roomIndex: number, ppltype: keyof roomQty) => {
            setRoomGuests(roomIndex, ppltype, true);
        },
        [setRoomGuests],
    );

    const changeRoomGuestsDown = useCallback(
        (roomIndex: number, ppltype: keyof roomQty) => {
            setRoomGuests(roomIndex, ppltype, false);
        },
        [setRoomGuests],
    );

    // Calculate the total number of adults and children across all rooms and return a string representation
    const getGuestsQty = useCallback(() => {
        // Calculate the total number of adults and children across all rooms
        const { adults, children } = roomQty.reduce(
            (totals, guest) => ({
                adults: totals.adults + guest.adults,
                children: totals.children + guest.children,
            }),
            { adults: 0, children: 0 },
        );

        // Create a string representation of the total number of adults and children with correct plural
        const adultsTxt = `${adults} Adult${adults === 1 ? '' : 's'}`;
        const childrenTxt =
            children <= config.limits.guests.children.min
                ? ''
                : `, ${children} Child${children > 1 ? 'ren' : ''}`;

        return `${adultsTxt}${childrenTxt}`;
    }, [roomQty, config]);

    //Renders the room quantity selector
    const qtyRender = useCallback(
        (
            numRooms: number,
            funcUp: () => void,
            funcDowm: () => void,
            limits: roomsLimit,
        ) => {
            return (
                <>
                    <span className={styles.qty}>
                        <button
                            onClick={funcUp}
                            type="button"
                            aria-label="Decrease number of cabins"
                            disabled={numRooms <= limits.min}
                        >
                            <Icons
                                iconClass="edit-minus"
                                extraClasses={['orange']}
                            />
                        </button>
                        <span aria-live="polite">{numRooms}</span>
                        <button
                            onClick={funcDowm}
                            type="button"
                            aria-label="Increase number of cabins"
                            disabled={numRooms >= limits.max}
                        >
                            <Icons
                                iconClass="edit-plus"
                                extraClasses={['orange']}
                            />
                        </button>
                    </span>
                </>
            );
        },
        [],
    );

    //Renders the guest quantity selector for each room
    const qtyRenderGuests = useCallback(
        (
            numAdults: number,
            numChildren: number,
            roomIndex: number,
            limits: guestsLimit,
        ) => {
            const overallGuests = numAdults + numChildren;
            return (
                <>
                    <div className={styles.qtySelector}>
                        <span className={styles.title}>Adults</span>
                        <span className={styles.qty}>
                            <button
                                onClick={() =>
                                    changeRoomGuestsDown(roomIndex, 'adults')
                                }
                                type="button"
                                aria-label={`Decrease adults in cabin ${roomIndex + 1}`}
                                disabled={numAdults <= limits.adults.min}
                            >
                                <Icons
                                    iconClass="edit-minus"
                                    extraClasses={['orange']}
                                />
                            </button>
                            <span aria-live="polite">{numAdults}</span>
                            <button
                                onClick={() =>
                                    changeRoomGuestsUp(roomIndex, 'adults')
                                }
                                type="button"
                                aria-label={`Increase adults in cabin ${roomIndex + 1}`}
                                disabled={
                                    numAdults >= limits.adults.max ||
                                    overallGuests >= limits.maxguests
                                }
                            >
                                <Icons
                                    iconClass="edit-plus"
                                    extraClasses={['orange']}
                                />
                            </button>
                        </span>
                    </div>
                    <div className={styles.qtySelector}>
                        <span className={styles.title}>Children (3 - 16)</span>
                        <span className={styles.qty}>
                            <button
                                onClick={() =>
                                    changeRoomGuestsDown(roomIndex, 'children')
                                }
                                type="button"
                                aria-label={`Decrease children in cabin ${roomIndex + 1}`}
                                disabled={numChildren <= limits.children.min}
                            >
                                <Icons
                                    iconClass="edit-minus"
                                    extraClasses={['orange']}
                                />
                            </button>
                            <span aria-live="polite">{numChildren}</span>
                            <button
                                onClick={() =>
                                    changeRoomGuestsUp(roomIndex, 'children')
                                }
                                type="button"
                                aria-label={`Increase children in cabin ${roomIndex + 1}`}
                                disabled={
                                    numChildren >= limits.children.max ||
                                    overallGuests >= limits.maxguests
                                }
                            >
                                <Icons
                                    iconClass="edit-plus"
                                    extraClasses={['orange']}
                                />
                            </button>
                        </span>
                    </div>
                </>
            );
        },
        [changeRoomGuestsDown, changeRoomGuestsUp],
    );

    return (
        <>
            <div
                className={`${styles.fieldWrapper} ${styles.fieldWrapperRoomGuests}`}
            >
                <div className={styles.label}>Cabins/Guests</div>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen(!open);
                    }}
                    aria-haspopup="true"
                    aria-expanded={open}
                    aria-controls={dialogId}
                    className={`${open ? styles.fieldWrapperActive : ''} ${styles.field} ${styles.input} rooms-guest`}
                >
                    <span>
                        <span>
                            {roomQty.length}{' '}
                            {roomQty.length === 1 ? 'Cabin ' : 'Cabins'}
                        </span>
                        <span className={styles.separator}> | </span>
                        <span>{getGuestsQty()}</span>
                    </span>
                    <span className={styles.toggle}>
                        <Icons
                            iconClass={`${open ? 'nav-arrow-up' : 'nav-arrow-down'}`}
                        />
                    </span>
                </button>
                <div
                    id={dialogId}
                    className={`${open ? styles.selectorOpen : styles.selectorClose} 
                    ${styles.selector}`}
                >
                    <div className={`${styles.wrapper} ${styles.margin}`}>
                        <div className={styles.qtySelector}>
                            <span className={styles.title}>
                                Cabins Required
                            </span>

                            {qtyRender(
                                roomQty.length,
                                changeRoomDown,
                                changeRoomUp,
                                config.limits.rooms,
                            )}
                        </div>
                        {roomQty.map((room, index) => (
                            <div className="roomsLooper" key={index}>
                                <div className={styles.roomTitle}>
                                    Cabin {config.numToWord[index]}
                                </div>
                                {qtyRenderGuests(
                                    room.adults,
                                    room.children,
                                    index,
                                    config.limits.guests,
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default BookingRoomsComponent;
